import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { ChallengeId } from '../../data/full/festival';
import { BOARD } from '../../data/full/path-layout';
import { t } from '../../shared/i18n';
import { challengeOf } from '../../sim/full/festival-scale';
import type { FestivalChallenge, SimState } from '../../sim/state';
import { worldToScreen } from '../camera';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { CORNER, TAIL, TAIL_HALF, bubble } from '../speaker-bubble';
import { cardOrigin, cardScale, isNear } from '../speaker-card';
import {
  LABEL_FONT_PX,
  LABEL_PAD,
  NAME_TEXTURE_SCALE,
  createLineTextures,
} from '../textures-names';
import { setTint } from '../util';
import {
  BURST_BEATS,
  HEIGHT,
  LANTERN,
  PIN,
  boardReturn,
  boardTurn,
  drawFace,
  drawPastilles,
  drawPosts,
  kindOf,
  picto,
  star,
} from './challenge-board';
import { PATH_COLORS } from './path-way';

const LINES: Readonly<Record<ChallengeId, () => string>> = {
  rapides: () => t('Les bad vibes courent plus vite'),
  nombreuses: () => t('Les bad vibes arrivent plus nombreuses'),
  boss: () => t('Le boss a plus de vie'),
};

interface BoardView {
  readonly root: Container;
  readonly burst: Graphics;
  readonly posts: Graphics;
  readonly face: Container;
  readonly planks: Graphics;
  readonly pastilles: Graphics;
  readonly lantern: Graphics;
  readonly bubble: BubbleView;
  key: string;
}

interface BubbleView {
  readonly root: Container;
  readonly panel: Graphics;
  readonly wash: Graphics;
  readonly rim: Graphics;
  readonly row: Graphics;
  readonly gain: Sprite;
  readonly line: Sprite;
  key: string;
  width: number;
  height: number;
  tipX: number;
}

const PAD = 9;
const ROW = 30;
const GAP = 10;
const PICTO = 26;
const STAR = 9;
const GAIN_SCALE = 1.3;
const WASH = 0.14;

const textWidth = (texture: Texture): number => texture.width / NAME_TEXTURE_SCALE - 2 * LABEL_PAD;

export function createChallenges(ctx: RenderContext): Family {
  const boardLayer = ctx.layers.speakers.addChild(new Container());
  const bubbleLayer = ctx.layers.screen.addChild(new Container());
  const texts = createLineTextures();
  const boards: BoardView[] = [];

  function boardAt(index: number): BoardView {
    let view = boards[index];
    if (view === undefined) {
      const root = boardLayer.addChild(new Container());
      const burst = root.addChild(new Graphics());
      const posts = root.addChild(new Graphics());
      drawPosts(posts);
      const face = root.addChild(new Container());
      const planks = face.addChild(new Graphics());
      const pastilles = face.addChild(new Graphics());
      const lantern = root.addChild(new Graphics());
      for (const side of [-1, 1]) {
        root
          .addChild(new Graphics())
          .circle(side * (BOARD.half + BOARD.post / 2), 0, 1.8)
          .fill(PIN)
          .stroke({ width: 0.8, color: PATH_COLORS.outline });
      }
      view = {
        root,
        burst,
        posts,
        face,
        planks,
        pastilles,
        lantern,
        bubble: makeBubble(),
        key: '',
      };
      boards[index] = view;
    }
    return view;
  }

  function makeBubble(): BubbleView {
    const root = bubbleLayer.addChild(new Container());
    const panel = root.addChild(new Graphics());
    const wash = root.addChild(new Graphics());
    const rim = root.addChild(new Graphics());
    const row = root.addChild(new Graphics());
    const text = () => {
      const sprite = root.addChild(new Sprite(Texture.EMPTY));
      sprite.anchor.set(0.5);
      sprite.scale.set(1 / NAME_TEXTURE_SCALE);
      return sprite;
    };
    const gain = text();
    gain.scale.set(GAIN_SCALE / NAME_TEXTURE_SCALE);
    return {
      root,
      panel,
      wash,
      rim,
      row,
      gain,
      line: text(),
      key: '',
      width: 0,
      height: 0,
      tipX: Number.NaN,
    };
  }

  // The cost picto in red, a divider, the gain in gold on top; one short line on the effect under.
  function layBubble(view: BubbleView, board: FestivalChallenge, frame: Frame): void {
    const kind = kindOf(board.id);
    const percent = Math.round(challengeOf(board.id).score * 100);
    view.gain.texture = texts.get(t('+{percent} %', { percent }), 700);
    view.line.texture = texts.get(LINES[kind](), 600);
    const gainWidth = textWidth(view.gain.texture) * GAIN_SCALE;
    const row = PICTO + 2 * GAP + 2 * STAR + 4 + gainWidth;
    const width = Math.ceil(Math.max(row, textWidth(view.line.texture))) + 2 * PAD;
    const height = PAD + ROW + LABEL_FONT_PX + PAD;
    const rowY = PAD + ROW / 2;
    let x = (width - row) / 2;
    const { palette } = frame;
    const g = view.row.clear();
    picto(g, kind, x + PICTO / 2, rowY + 1, 0.95, {
      body: 0x3a0d14,
      rim: palette.rouge,
      crown: palette.rouge,
    });
    x += PICTO + GAP;
    g.moveTo(x, rowY - 9)
      .lineTo(x, rowY + 9)
      .stroke({ width: 1.2, color: palette.mage, alpha: 0.3 });
    x += GAP;
    star(g, x + STAR, rowY, STAR, palette.or);
    x += 2 * STAR + 4;
    view.gain.position.set(Math.round(x + gainWidth / 2), rowY);
    view.line.position.set(Math.round(width / 2), Math.round(PAD + ROW + LABEL_FONT_PX / 2));
    view.width = width;
    view.height = height;
    view.tipX = Number.NaN;
  }

  function shapeBubble(view: BubbleView, tipX: number): void {
    if (tipX === view.tipX) {
      return;
    }
    view.tipX = tipX;
    const { width, height } = view;
    bubble(view.panel.clear(), width, height, tipX, false);
    view.panel.fill(0xffffff);
    bubble(view.wash.clear(), width, height, tipX, false);
    view.wash.fill(0xffffff);
    bubble(view.rim.clear(), width, height, tipX, false);
    view.rim.stroke({ width: 1.8, color: 0xffffff });
  }

  function showBubble(view: BubbleView, board: FestivalChallenge, frame: Frame): void {
    const { palette } = frame;
    const key = `${board.id} ${String(palette.rouge)} ${String(palette.or)} ${String(palette.mage)}`;
    if (view.key !== key) {
      view.key = key;
      layBubble(view, board, frame);
    }
    const { camera } = frame;
    const anchor = worldToScreen(camera, { x: board.x, y: board.y - HEIGHT / 2 - 8 });
    const scale = cardScale(view.width, camera.screenWidth, camera.screenHeight);
    const origin = cardOrigin(
      anchor.x,
      anchor.y - TAIL * scale,
      view.width * scale,
      view.height * scale,
      camera.screenWidth,
      camera.screenHeight,
      false,
    );
    const reach = CORNER + TAIL_HALF;
    shapeBubble(
      view,
      Math.round(Math.max(reach, Math.min(view.width - reach, (anchor.x - origin.x) / scale))),
    );
    setTint(view.panel, palette.solClair);
    setTint(view.wash, palette.mage);
    setTint(view.rim, palette.mage);
    setTint(view.gain, palette.or);
    setTint(view.line, palette.texte);
    view.panel.alpha = frame.light.additive ? 0.88 : 0.94;
    view.wash.alpha = WASH;
    view.root.visible = true;
    view.root.scale.set(scale);
    view.root.position.set(origin.x, origin.y);
  }

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      const shown = state.festival?.phase === 'path' ? (state.festival.challenges ?? []) : [];
      boards.forEach((view, index) => {
        view.root.visible = index < shown.length;
        view.bubble.root.visible = false;
      });
      const { palette, tempo } = frame;
      shown.forEach((board, index) => {
        const view = boardAt(index);
        view.root.position.set(board.x, board.y);
        const since = (tick: number): number => (frame.now - tick) / tempo.ticksPerBeat;
        const beats = board.takenTick === undefined ? undefined : since(board.takenTick);
        const turn =
          beats === undefined && board.droppedTick !== undefined
            ? boardReturn(since(board.droppedTick), frame.calm)
            : boardTurn(beats, frame.calm);
        const back = Math.cos(turn) < 0;
        const kind = kindOf(board.id);
        const key = `${kind} ${String(back)} ${String(palette.mage)} ${String(palette.badVibe)}`;
        if (view.key !== key) {
          view.key = key;
          drawFace(view.planks.clear(), kind, back, frame);
        }
        view.face.scale.y = Math.max(0.05, Math.abs(Math.cos(turn)));
        drawPastilles(view.pastilles.clear(), board, state, ctx, frame);

        const lit = beats !== undefined;
        const burst = view.burst.clear();
        if (lit && !frame.calm && beats < BURST_BEATS) {
          const k = beats / BURST_BEATS;
          burst.circle(0, 0, 30 + 34 * k).stroke({ width: 2.5, color: palette.mage, alpha: 1 - k });
        }
        const hookX = BOARD.half + BOARD.post / 2 + 7;
        const lamp = lit ? palette.mage : LANTERN;
        view.lantern
          .clear()
          .moveTo(hookX, -HEIGHT / 2 - 2)
          .lineTo(hookX, -HEIGHT / 2 + 2)
          .stroke({ width: 0.8, color: 0x1a120c })
          .circle(hookX, -HEIGHT / 2 + 7, 9)
          .fill({ color: lamp, alpha: 0.22 })
          .ellipse(hookX, -HEIGHT / 2 + 7, 3.6, 4.4)
          .fill(lamp)
          .stroke({ width: 1.1, color: PATH_COLORS.outline });

        if (isNear(board, state.players)) {
          showBubble(view.bubble, board, frame);
        }
      });
    },
    reset(): void {
      for (const view of boards) {
        view.key = view.bubble.key = '';
      }
    },
    destroy(): void {
      texts.destroy();
      boardLayer.destroy({ children: true });
      bubbleLayer.destroy({ children: true });
    },
  };
}
